import { useCallback, useEffect, useState } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import styled from 'styled-components';
import urls from 'urls';
import { Header } from 'components/basic/header';
import { DesignProvider } from 'components/basic/designProvider';
import { Auth } from 'components/pages/auth';
import { Error } from 'components/pages/error';
import { NoMatch } from 'components/pages/noMatch';
import { WorkflowList } from 'components/pages/workflowList';
import {
  LoginContextProvider,
  useLoginContext,
} from 'components/providers/loginContext';
import { WorkflowAppsContextProvider } from 'components/providers/workflowAppsContext';
import { StartService } from 'components/pages/startService';
import { Overlay } from 'components/basic/overlay';
import { Loading } from 'components/basic/loading';
import { Retry } from 'components/pages/retry';
import { Settings } from 'components/pages/settings';
import { Help } from 'components/pages/help';
import { useToken } from 'feature/token';
import { RSI_AUTHORIZATION } from 'constants/constants';
import { useCode } from 'feature/code';
import { RsiAuthorizeParameter } from 'api/tokenApi';

const Overall = styled.div`
  display: flex;
  height: calc(100vh - 88px);
  @media (min-width: 960px) {
    height: calc(100vh - 62px);
  }
  @media (max-width: 959px) {
    display: block;
  }
`;

const LoadingOverlay = styled.div`
  width: 100vw;
`;

// セッションストレージに保存する認証情報のキー
const SESSION_KEY_AUT_AUTHORIZE = 'rsiAuthorizeParameter';

// セッションストレージにwfIdを保持するためのキー
const SESSION_KEY_SETTINGS_WFID = 'settingsWfId';

export const ServicePages = () => {
  const [
    handleGetToken,
    handleRegisterToken,
    exitToken,
    tokenLoading,
    getError,
    registerError,
  ] = useToken();
  const [handleGetCode, query, codeLoading, codeError] = useCode();
  // getRsiTokenのエラー
  const [rsiError, setRsiError] = useState<any>(undefined);

  // URLを書き換えた後の状態を反映したいためコンポーネント内で定義
  const redirectUri = window.location.href;

  const LoginContext = useLoginContext();

  const getToken = useCallback(async () => {
    await handleGetToken();
  }, [handleGetToken]);

  // 初期表示時
  useEffect(() => {
    (async () => {
      // settingsの場合はwfId情報を削除するので、sessionStorageに保持しておく
      if (
        redirectUri.includes('settings') &&
        !sessionStorage.getItem(SESSION_KEY_SETTINGS_WFID)
      ) {
        const arrRedirectUri = redirectUri.split('/');
        sessionStorage.setItem(
          SESSION_KEY_SETTINGS_WFID,
          arrRedirectUri[arrRedirectUri.length - 1],
        );
      }

      // 認証情報が存在しない場合はリフレッシュトークンを取得
      if (!sessionStorage.getItem(SESSION_KEY_AUT_AUTHORIZE)) {
        handleGetToken();
        return;
      }

      // 認証情報が存在する場合はアクセストークン取得を行う
      const authorizeParameter: RsiAuthorizeParameter = JSON.parse(
        sessionStorage.getItem(SESSION_KEY_AUT_AUTHORIZE) as any,
      );

      // codeを取得
      authorizeParameter.code = window.location.search.substring(6).toString();

      try {
        // 末尾の認可コードを削除
        const uri = new URL(redirectUri);
        let replacedPathname = uri.pathname.replace(/\?.*$/, '');
        // settingsの場合、wfId情報が削除されているため末尾に付け直す
        if (replacedPathname.includes('settings')) {
          replacedPathname = `${replacedPathname}/${sessionStorage.getItem(
            SESSION_KEY_SETTINGS_WFID,
          )}`;
        }
        // eslint-disable-next-line no-restricted-globals
        history.replaceState('', '', replacedPathname);

        // リフレッシュトークンを登録して再度リフレッシュトークン取得処理を行う
        await handleRegisterToken(authorizeParameter);
        await handleGetToken();
      } catch (e) {
        sessionStorage.removeItem(SESSION_KEY_AUT_AUTHORIZE);
        setRsiError(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 発生したエラーに応じたリトライ処理
  const retryMethod = useCallback(() => {
    // リフレッシュトークン取得に失敗している場合は再実行
    if (getError) {
      getToken();
      return;
    }

    // リフレッシュトークン登録かアクセストークン取得に失敗している場合はコード取得から認証画面へ遷移
    if (registerError || rsiError) {
      handleGetCode(redirectUri);
      return;
    }
  }, [getError, registerError, rsiError, getToken, handleGetCode, redirectUri]);

  // handleGetCodeが呼ばれた後の認証処理
  useEffect(() => {
    // データ取得が終わっていない時は終了
    if (!query) return;
    // query からクライアントIDを取得
    const arr = query.replace(/^\?/, '').split('&');

    const resObj: any = {};
    arr.forEach((item) => {
      const keyValueArr = item.split('=');
      resObj[keyValueArr[0]] = keyValueArr[1];
    });

    // settingsの場合はredirectUriからwfIdを削除
    let replaceRedirectUri = redirectUri;
    if (
      replaceRedirectUri.includes('settings') &&
      sessionStorage.getItem(SESSION_KEY_SETTINGS_WFID)
    ) {
      replaceRedirectUri = replaceRedirectUri.replace(
        `/${sessionStorage.getItem(SESSION_KEY_SETTINGS_WFID)}` as string,
        '',
      );
    }

    // セッションストレージに登録
    const authorizeParameter = {
      redirectUri: replaceRedirectUri,
    };
    sessionStorage.setItem(
      SESSION_KEY_AUT_AUTHORIZE,
      JSON.stringify(authorizeParameter),
    );

    // 認証画面ロード
    window.location.assign(RSI_AUTHORIZATION + query);
  }, [query, redirectUri]);

  // トークン更新判定用パラメータの削除処理
  useEffect(() => {
    if (exitToken && sessionStorage.getItem(SESSION_KEY_AUT_AUTHORIZE)) {
      sessionStorage.removeItem(SESSION_KEY_AUT_AUTHORIZE);
      // トークンの更新がされるタイミングでwfId情報も不要になるので削除
      sessionStorage.removeItem(SESSION_KEY_SETTINGS_WFID);
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exitToken]);

  // API実行前
  if (exitToken === undefined && !getError && !registerError && !rsiError) {
    return (
      <LoadingOverlay>
        <DesignProvider>
          <Overlay>
            <Loading />
          </Overlay>
        </DesignProvider>
      </LoadingOverlay>
    );
  } else if (getError || rsiError || registerError) {
    return (
      <DesignProvider>
        <Retry
          loading={tokenLoading || codeLoading}
          retryMethod={retryMethod}
        />
      </DesignProvider>
    );
  }

  if (exitToken && LoginContext.roles.includes('PowerUser')) {
    const arrRedirectUri = redirectUri.split('/');
    const replacedUri = redirectUri.replace(/\?.*$/, '');
    let replaceRedirectUri = redirectUri;

    if (
      sessionStorage.getItem(SESSION_KEY_SETTINGS_WFID) &&
      replacedUri.includes('settings') &&
      arrRedirectUri[arrRedirectUri.length - 1] !== 'settings'
    ) {
      replaceRedirectUri = replacedUri.replace(
        `/${sessionStorage.getItem(SESSION_KEY_SETTINGS_WFID)}` as string,
        '',
      );
    }

    // トークンを更新するか判定
    // リダイレクト時はクエリが付くため、その場合はhandleGetCodeを実行しない
    if (
      !sessionStorage.getItem(SESSION_KEY_AUT_AUTHORIZE) &&
      !arrRedirectUri[arrRedirectUri.length - 1].includes('code')
    ) {
      handleGetCode(replaceRedirectUri);
    }
    return (
      <Overall>
        <DesignProvider>
          <BrowserRouter>
            <Routes>
              <Route path={urls.WORKFLOW_LIST} element={<WorkflowList />} />
              <Route path={`${urls.SETTINGS}/:wfId`} element={<Settings />} />
              <Route path={`${urls.HELP}`} element={<Help />} />
              <Route path="*" element={<NoMatch />} />
            </Routes>
          </BrowserRouter>
        </DesignProvider>
      </Overall>
    );
  } else if (
    LoginContext.roles.length !== 0 &&
    !LoginContext.roles.includes('PowerUser')
  ) {
    return (
      <DesignProvider>
        <BrowserRouter>
          <Error />
        </BrowserRouter>
      </DesignProvider>
    );
  } else if (!exitToken) {
    return (
      <DesignProvider>
        <BrowserRouter>
          <StartService
            handleGetCode={handleGetCode}
            codeLoading={codeLoading}
            codeError={codeError}
            redirectUri={redirectUri}
          />
        </BrowserRouter>
      </DesignProvider>
    );
  }
  return null;
};

export const App = () => {
  return (
    <div className="src-app">
      <LoginContextProvider>
        <WorkflowAppsContextProvider>
          <Auth>
            <Header />
            <ServicePages />
          </Auth>
        </WorkflowAppsContextProvider>
      </LoginContextProvider>
    </div>
  );
};
